import { useEffect, useMemo, useState } from "react";
import {
  Building2,
  CalendarDays,
  Database,
  ExternalLink,
  Factory,
  Layers3,
  ListPlus,
  ScanSearch,
  ShieldAlert,
  Sparkles,
  Target,
  Users,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Badge, Button, Spinner } from "../components/Ui";
import {
  BusinessMixDonut,
  DemandBars,
  EarningsForecast,
  FinancialTrendChart,
  PositionBars,
  ResearchScore,
  Sparkline,
} from "../components/ResearchCharts";
import { companyBrief, industryBrief, researchSources, type ResearchTone } from "../data/research";
import type { CompanyBrief, IndustryBrief, ResearchBundle, ResearchSource } from "../../shared/types";
import { api } from "../lib/api";
import { useData } from "../lib/data";
import "../one-pager.css";
import "../one-pager-extras.css";

type ResearchMode = "company" | "industry";

function ToneValue({ value, tone }: { value: string; tone: ResearchTone }) {
  return <span className={`research-change tone-${tone}`}>{value}</span>;
}

function PanelTitle({ icon, title, meta }: { icon: React.ReactNode; title: string; meta?: string }) {
  return <div className="research-panel-title"><div>{icon}<h2>{title}</h2></div>{meta&&<span>{meta}</span>}</div>;
}

const initialBundle: ResearchBundle = {
  id: "verified-boe-sample",
  query: "京东方A",
  company: companyBrief,
  industry: industryBrief,
  sources: researchSources,
  generatedAt: "2026-07-18T11:20:00+08:00",
  model: "人工校验样例",
  dataStatus: "verified-sample",
  disclaimer: "京东方样例中的公告和行业事实已按来源校验；预测与估值仍为演示口径，不构成投资建议。",
};

function SourceLedger({ sources, disclaimer, compact = false }: { sources: ResearchSource[]; disclaimer: string; compact?: boolean }) {
  return <section className={`research-panel source-ledger ${compact ? "compact" : ""}`}>
    <PanelTitle icon={<Database size={15}/>} title="证据与来源" meta="按权威性排序"/>
    <div className="source-ledger-list">{sources.map((source, index) => <a key={`${source.url}-${index}`} href={source.url} target="_blank" rel="noreferrer">
      <b>{String(index + 1).padStart(2, "0")}</b><div><strong>{source.label}</strong><span>{source.type} · {source.date}</span></div><ExternalLink size={13}/>
    </a>)}</div>
    <p className="source-disclaimer">{disclaimer}</p>
  </section>;
}

function CompanyView({ data, sources, disclaimer }: { data: CompanyBrief; sources: ResearchSource[]; disclaimer: string }) {
  return <>
    <section className="research-hero company-research-hero">
      <div className="research-identity">
        <div className="research-overline">COMPANY DOSSIER · 01</div>
        <div className="identity-line"><h2>{data.identity.name}</h2><span>{data.identity.code}</span></div>
        <p>{data.identity.industry} <i/> {data.identity.position}</p>
        <div className="research-tags">{data.identity.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
      </div>
      <div className="research-hero-view"><ResearchScore value={data.view.score} label={data.view.label}/><div><span>当前判断</span><strong>{data.view.label}</strong><small>置信度：{data.view.confidence}</small></div></div>
      <div className="research-meta"><span>更新 {data.identity.updatedAt}</span><span>{data.identity.coverage}</span></div>
    </section>

    <section className="snapshot-grid">{data.snapshot.map((item) => <article key={item.label}>
      <div className="snapshot-head"><span>{item.label}</span><ToneValue value={item.change} tone={item.tone}/></div>
      <div className="snapshot-value"><strong>{item.value}</strong><em>{item.unit}</em></div>
      <div className="snapshot-foot"><small>{item.note}</small><Sparkline values={item.series} tone={item.tone}/></div>
    </article>)}</section>

    <section className="research-thesis-strip">
      <div><Target size={16}/><span>核心判断</span></div><p>{data.view.thesis}</p>
      <div className="counter-view"><span>反方证据</span><p>{data.view.counter}</p></div>
    </section>

    <div className="research-grid">
      <section className="research-panel span-7">
        <PanelTitle icon={<ScanSearch size={15}/>} title="财务拐点与利润质量" meta={`${data.financialTrend[0]?.period ?? "历史"}–${data.financialTrend.at(-1)?.period ?? "最新"}`}/>
        <FinancialTrendChart data={data.financialTrend} label={data.identity.name}/>
        <div className="financial-footnotes">{data.financialHighlights.map((item) => <span key={item.label}>{item.label}<b className={item.tone ? `tone-${item.tone}` : ""}>{item.value}</b></span>)}</div>
      </section>
      <section className="research-panel span-5">
        <PanelTitle icon={<Layers3 size={15}/>} title="业务结构" meta="收入 / 业务敞口"/>
        <BusinessMixDonut items={data.businessMix} totalLabel={data.businessMixTotal}/>
        <p className="panel-note">{data.businessMixNote}</p>
      </section>

      <section className="research-panel span-12">
        <PanelTitle icon={<Target size={15}/>} title="投资逻辑：从验证到兑现" meta="事实 → 判断 → 里程碑"/>
        <div className="logic-lanes">{data.logic.map((item, index) => <article key={item.horizon}>
          <b>{String(index + 1).padStart(2, "0")}</b><div><span>{item.horizon}</span><h3>{item.title}</h3><p>{item.detail}</p><small>{item.evidence}</small></div><i className={`logic-signal signal-${item.tone}`}/>
        </article>)}</div>
      </section>

      <section className="research-panel span-7">
        <PanelTitle icon={<Users size={15}/>} title="公司在行业中的位置" meta="相对强度指数 · 文字结论待核验"/>
        <PositionBars items={data.marketPosition}/>
        <div className="position-callout"><strong>结论</strong><p>{data.positionConclusion}</p></div>
      </section>
      <section className="research-panel span-5">
        <PanelTitle icon={<CalendarDays size={15}/>} title="催化剂与验证时间轴" meta="未来 24 个月"/>
        <div className="catalyst-timeline">{data.timeline.map((item) => <div key={`${item.date}-${item.title}`}><time>{item.date}</time><i className={`timeline-${item.tone}`}/><p><strong>{item.title}</strong><span>{item.status}</span></p></div>)}</div>
      </section>

      <section className="research-panel span-12">
        <PanelTitle icon={<Building2 size={15}/>} title="同业坐标系" meta="谁是对手、谁是技术标杆"/>
        <div className="research-table-wrap"><table className="research-table peer-table"><thead><tr><th>公司</th><th>角色</th><th>核心敞口</th><th>相对优势</th><th>研究定位</th></tr></thead><tbody>{data.peers.map((peer, index) => <tr key={`${peer.code}-${index}`} className={peer.name === data.identity.name || index === 0 ? "featured-row" : ""}><td><strong>{peer.name}</strong><small>{peer.code}</small></td><td>{peer.role}</td><td>{peer.focus}</td><td>{peer.edge}</td><td><Badge tone={peer.signal === "强对手" ? "down" : peer.signal === "基准" ? "up" : "neutral"}>{peer.signal}</Badge></td></tr>)}</tbody></table></div>
      </section>

      <section className="research-panel span-7">
        <PanelTitle icon={<ScanSearch size={15}/>} title="盈利预测" meta={data.forecastLabel}/>
        <EarningsForecast items={data.earningsForecast}/>
      </section>
      <section className="research-panel span-5 valuation-panel">
        <PanelTitle icon={<Target size={15}/>} title="估值框架" meta="行情快照 + 研究框架"/>
        <div className="valuation-metrics">{data.valuation.map((item) => <article key={item.label}><span>{item.label}</span><strong>{item.value}</strong><small>{item.note}</small></article>)}</div>
        <p>{data.valuationNote}</p>
      </section>

      <section className="research-panel span-5">
        <PanelTitle icon={<Factory size={15}/>} title="客户与供应链" meta="商业质量观察"/>
        <div className="chain-block"><span>核心客户群</span><div>{data.chain.customers.map((item) => <b key={item}>{item}</b>)}</div></div>
        <div className="chain-block"><span>关键供应环节</span><div>{data.chain.suppliers.map((item) => <b key={item}>{item}</b>)}</div></div>
      </section>
      <section className="research-panel span-7">
        <PanelTitle icon={<ScanSearch size={15}/>} title="下一次调研必须问什么" meta="把故事变成可证伪问题"/>
        <ol className="fieldwork-list">{data.questions.map((question, index) => <li key={question}><b>{String(index + 1).padStart(2, "0")}</b><span>{question}</span></li>)}</ol>
      </section>

      <section className="research-panel outlook-panel span-12">
        <PanelTitle icon={<Target size={15}/>} title="研究员观点与行动框架" meta="短期逻辑 · 长期逻辑 · 催化 · 公司计划"/>
        <div className="outlook-theses">
          {[data.outlook.shortTerm, data.outlook.longTerm].map((section, index) => <article key={section.title}>
            <span>{index === 0 ? "SHORT TERM" : "LONG TERM"}</span><h3>{section.title}</h3><p>{section.thesis}</p>
            <div><dl><dt>支持证据</dt>{section.evidence.map((item) => <dd key={item}>{item}</dd>)}</dl><dl><dt>持续跟踪</dt>{section.watch.map((item) => <dd key={item}>{item}</dd>)}</dl></div>
          </article>)}
        </div>
        <div className="outlook-detail-grid">
          <div><h3>近期催化与验证</h3>{data.outlook.catalysts.map((item) => <article key={`${item.date}-${item.event}`}><time>{item.date}</time><div><strong>{item.event}</strong><p>{item.impact}</p><small>验证：{item.verification}</small></div></article>)}</div>
          <div><h3>公司计划与业务演进</h3>{data.outlook.plans.map((item) => <article key={item.area}><b>{item.area}</b><div><strong>{item.detail}</strong><small>验证：{item.verification}</small></div></article>)}</div>
        </div>
        <div className="outlook-conclusion"><span>综合判断</span><p>{data.outlook.conclusion}</p></div>
      </section>

      <section className="research-panel risk-panel span-12">
        <PanelTitle icon={<ShieldAlert size={15}/>} title="风险雷达" meta="按发生概率 × 影响排序"/>
        <div className="risk-register">{data.risks.map((risk) => <article key={risk.title}><span>{risk.level}</span><div><strong>{risk.title}</strong><p>{risk.detail}</p></div></article>)}</div>
      </section>
      <div className="span-12"><SourceLedger sources={sources} disclaimer={disclaimer}/></div>
    </div>
  </>;
}

function IndustryView({ data, sources, disclaimer }: { data: IndustryBrief; sources: ResearchSource[]; disclaimer: string }) {
  return <>
    <section className="research-hero industry-research-hero">
      <div className="research-identity">
        <div className="research-overline">INDUSTRY DOSSIER · 01</div>
        <div className="identity-line"><h2>{data.identity.name}</h2></div>
        <p>{data.identity.code}</p>
        <div className="research-tags">{data.identity.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
      </div>
      <div className="research-hero-view"><ResearchScore value={data.view.score} label={data.view.label}/><div><span>周期定位</span><strong>{data.identity.stage}</strong><small>{data.view.label}</small></div></div>
      <div className="research-meta"><span>更新 {data.identity.updatedAt}</span><span>{data.identity.coverage}</span></div>
    </section>

    <section className="snapshot-grid industry-snapshot">{data.metrics.map((item) => <article key={item.label}>
      <div className="snapshot-head"><span>{item.label}</span><ToneValue value={item.change} tone={item.tone}/></div>
      <div className="snapshot-value"><strong>{item.value}</strong><em>{item.unit}</em></div>
    </article>)}</section>

    <section className="research-thesis-strip industry-thesis"><div><Target size={16}/><span>行业判断</span></div><p>{data.view.thesis}</p></section>

    <section className="segment-grid">{data.segments.map((segment) => <article key={segment.name}>
      <div className="segment-head"><div><span>{segment.stage}</span><h3>{segment.name}</h3></div><b className={`tone-${segment.tone}`}>{segment.score}</b></div>
      <dl><div><dt>需求</dt><dd>{segment.demand}</dd></div><div><dt>供给</dt><dd>{segment.supply}</dd></div><div><dt>盈利</dt><dd>{segment.margin}</dd></div></dl>
      <div className="segment-meter"><i style={{ width: `${segment.score}%` }}/></div>
    </article>)}</section>

    <div className="research-grid">
      <section className="research-panel span-6">
        <PanelTitle icon={<ScanSearch size={15}/>} title="需求增速雷达" meta="指数化：基期 = 100"/>
        <DemandBars items={data.demandTrend}/>
        <p className="panel-note">{data.demandNote}</p>
      </section>
      <section className="research-panel span-6">
        <PanelTitle icon={<Layers3 size={15}/>} title="产业链利润迁移" meta="材料 → 制造 → 终端"/>
        <div className="value-chain">{data.valueChain.map((stage, index) => <article key={stage.stage}><b>{index + 1}</b><div><h3>{stage.stage}</h3><p>{stage.companies.join(" · ")}</p><span>{stage.signal}</span></div></article>)}</div>
      </section>

      <section className="research-panel span-12">
        <PanelTitle icon={<Users size={15}/>} title="龙头与代表性公司" meta="把行业判断映射到资产"/>
        <div className="research-table-wrap"><table className="research-table leader-table"><thead><tr><th>公司</th><th>核心敞口</th><th>行业位置</th><th>下一验证点</th></tr></thead><tbody>{data.leaders.map((leader) => <tr key={leader.code}><td><strong>{leader.company}</strong><small>{leader.code}</small></td><td>{leader.exposure}</td><td>{leader.position}</td><td>{leader.watch}</td></tr>)}</tbody></table></div>
      </section>

      <section className="research-panel span-6 catalyst-panel">
        <PanelTitle icon={<CalendarDays size={15}/>} title="正向催化" meta="未来 6–18 个月"/>
        <ol>{data.catalysts.map((item, index) => <li key={item}><b>{String(index + 1).padStart(2, "0")}</b><span>{item}</span></li>)}</ol>
      </section>
      <section className="research-panel span-6 risk-panel">
        <PanelTitle icon={<ShieldAlert size={15}/>} title="反向证据" meta="出现即下修判断"/>
        <ol>{data.risks.map((item, index) => <li key={item}><b>{String(index + 1).padStart(2, "0")}</b><span>{item}</span></li>)}</ol>
      </section>
      <div className="span-12"><SourceLedger sources={sources} disclaimer={disclaimer}/></div>
    </div>
  </>;
}

export function OnePagerPage() {
  const { data: workspace, refresh } = useData();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<ResearchMode>("company");
  const [query, setQuery] = useState("");
  const [bundle, setBundle] = useState<ResearchBundle>(initialBundle);
  const [recent, setRecent] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem("alphaos-recent-research") || "[]") as string[]; } catch { return []; }
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [queued, setQueued] = useState(false);
  const templates = useMemo(() => (workspace?.templates ?? []).filter((item) => item.workflow === mode), [workspace?.templates, mode]);

  useEffect(() => {
    const preferred = templates.find((item) => item.isDefault) ?? templates[0];
    if (preferred && !templates.some((item) => item.id === selectedTemplateId)) setSelectedTemplateId(preferred.id);
  }, [templates, selectedTemplateId]);

  useEffect(() => {
    const incoming = searchParams.get("q")?.trim();
    if (incoming && incoming.length >= 2 && incoming !== bundle.query) void run(incoming);
  }, []);

  const switchMode = (next: ResearchMode) => {
    setMode(next);
    setError("");
    setQueued(false);
  };

  const run = async (target = query, refresh = false) => {
    const cleaned = target.trim();
    if (cleaned.length < 2) { setError("请输入至少两个字的公司名称或证券代码"); return; }
    setBusy(true);
    setError("");
    try {
      const response = await api.generateResearchReport(cleaned, refresh, selectedTemplateId || undefined);
      setBundle(response);
      setMode("company");
      setQuery(cleaned);
      setQueued(false);
      const nextRecent = [response.company.identity.name, ...recent.filter((item) => item !== response.company.identity.name)].slice(0, 5);
      setRecent(nextRecent);
      localStorage.setItem("alphaos-recent-research", JSON.stringify(nextRecent));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "研究生成失败，请稍后重试");
    } finally {
      setBusy(false);
    }
  };

  const queueResearch = async () => {
    await api.saveMemory({
      title: `${bundle.company.identity.name} · 一页纸后续验证`,
      thesis: bundle.company.view.thesis,
      evidence: `${bundle.sources.length}项来源；反向证据：${bundle.company.view.counter}`,
      status: "待验证",
      confidence: bundle.company.view.score,
      decisionType: mode === "company" ? "公司研究" : "行业研究",
      asset: mode === "company" ? bundle.company.identity.name : bundle.industry.identity.name,
      tags: mode === "company" ? bundle.company.identity.tags : bundle.industry.identity.tags,
      reviewDate: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
      source: `一页纸研究 · ${bundle.model}`,
    });
    await refresh();
    setQueued(true);
  };

  return <div className="page-stack one-pager-page">
    <div className="page-title research-page-title"><div><span className="eyebrow">INSTITUTIONAL RESEARCH WORKBENCH</span><h1>一页纸研究</h1><p>{workspace?.profile.portfolioName || "机构组合"} · 输入任意公司，自动生成联动的公司底稿与所属行业研究页。</p></div><div className="research-page-status"><Badge tone="dark">LIVE · PUBLIC DATA + AI</Badge><span>{bundle.cacheHit ? "已复用 24 小时缓存" : `更新 ${bundle.company.identity.updatedAt}`}</span></div></div>

    <div className="research-toolbar">
      <div className="segmented research-segmented"><button className={mode === "company" ? "active" : ""} onClick={() => switchMode("company")}><Building2 size={16}/>公司一页纸</button><button className={mode === "industry" ? "active" : ""} onClick={() => switchMode("industry")}><Factory size={16}/>行业一页纸</button></div>
      <div className="research-query"><label><span>公司名称 / 证券代码</span><input value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && !busy) void run(); }} placeholder="例如：兴业银行 / 601166"/></label><label className="research-template-select"><span>生成协议</span><select value={selectedTemplateId} onChange={(event) => setSelectedTemplateId(event.target.value)}>{templates.map((template) => <option key={template.id} value={template.id}>{template.name}{template.isDefault ? " · 默认" : ""}</option>)}</select></label><Button onClick={() => void run()} disabled={busy}><Sparkles size={15}/>{busy ? <Spinner label="正在生成完整研究"/> : "生成完整研究"}</Button></div>
    </div>
    <div className="research-context-bar">
      <div><span>当前报告</span><strong>{bundle.company.identity.name}</strong><b>{bundle.company.identity.code}</b><i>→</i><strong>{bundle.industry.identity.name}</strong></div>
      <div className="research-recents"><span>最近研究</span>{recent.map((item) => <button key={item} disabled={busy} onClick={() => void run(item)}>{item}</button>)}{bundle.dataStatus === "generated-draft"&&<button disabled={busy} onClick={() => void run(bundle.query, true)}>重新生成</button>}<button className="research-queue-button" onClick={() => void queueResearch()} disabled={queued}><ListPlus size={13}/>{queued ? "已加入复盘队列" : "加入研究队列"}</button></div>
    </div>
    {busy&&<div className="research-generating"><Spinner label="正在识别公司、读取公开数据，并生成公司与行业双页报告…"/><span>通常需要 20–60 秒，请保持当前页面打开</span></div>}
    {error&&<div className="research-error">{error}</div>}

    {mode === "company" ? <CompanyView data={bundle.company} sources={bundle.sources} disclaimer={bundle.disclaimer}/> : <IndustryView data={bundle.industry} sources={bundle.sources} disclaimer={bundle.disclaimer}/>}
  </div>;
}
